import { useState, useRef, useEffect } from 'react';
import type { CustomerListItem } from '../types';

interface CustomerSelectorProps {
  customers: CustomerListItem[];
  selectedCustomerId: string | undefined;
  selectedCustomerName?: string; // Fallback name when customer not in list
  onSelect: (customerId: string) => void;
  onSearch: (term: string) => void;
  onClearSearch: () => void;
  isLoading: boolean;
  isSearching: boolean;
  disabled?: boolean;
  error?: string;
}

export function CustomerSelector({
  customers,
  selectedCustomerId,
  selectedCustomerName,
  onSelect,
  onSearch,
  onClearSearch,
  isLoading,
  isSearching,
  disabled = false,
  error,
}: CustomerSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Find selected customer in list, or use fallback info
  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);
  // Consider selection valid if we have an ID and either found the customer or have a fallback name
  const hasSelection = Boolean(selectedCustomerId) && (selectedCustomer !== undefined || Boolean(selectedCustomerName));

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle search input
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.trim()) {
      onSearch(value);
    } else {
      onClearSearch();
    }
  };

  // Handle customer selection
  const handleSelect = (customer: CustomerListItem) => {
    onSelect(customer.id);
    setSearchTerm('');
    onClearSearch();
    setIsOpen(false);
  };

  // Clear selection
  const handleClear = () => {
    onSelect('');
    setSearchTerm('');
    onClearSearch();
  };

  return (
    <div className="w-full relative" ref={dropdownRef}>
      <label className="input-label">
        Select Customer <span className="text-red-500">*</span>
      </label>

      {/* Selected Customer Display or Search Input */}
      {hasSelection && !isOpen ? (
        <div
          className={`flex items-center justify-between p-3 border rounded-lg bg-white
                     ${error ? 'border-red-500' : 'border-secondary-300'}
                     ${disabled ? 'bg-secondary-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={() => !disabled && setIsOpen(true)}
        >
          <div>
            <p className="font-medium text-secondary-900">
              {selectedCustomer?.full_name || selectedCustomerName}
            </p>
            {selectedCustomer?.email && (
              <p className="text-sm text-secondary-500">{selectedCustomer.email}</p>
            )}
            {selectedCustomer?.billing_city && selectedCustomer?.billing_province && (
              <p className="text-xs text-secondary-400 mt-0.5">
                {selectedCustomer.billing_city}, {selectedCustomer.billing_province}
              </p>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 text-secondary-400 hover:text-secondary-600 transition-colors"
              aria-label="Clear selection"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => setIsOpen(true)}
            placeholder="Search by name or email..."
            className={`input-field pr-10 ${error ? 'input-error' : ''}`}
            disabled={disabled}
          />
          {/* Search/Loading Icon */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {isSearching ? (
              <svg className="animate-spin w-5 h-5 text-secondary-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Dropdown List */}
      {isOpen && !hasSelection && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-secondary-500">
              Loading customers...
            </div>
          ) : customers.length === 0 ? (
            <div className="p-4 text-center text-secondary-500">
              {searchTerm ? 'No customers found' : 'No customers available'}
            </div>
          ) : (
            <ul className="py-1">
              {customers.map((customer) => (
                <li key={customer.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(customer)}
                    className="w-full px-4 py-3 text-left hover:bg-secondary-50 transition-colors
                               flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-secondary-900">
                        {customer.full_name}
                      </p>
                      <p className="text-sm text-secondary-500">
                        {customer.email}
                      </p>
                      {customer.billing_city && customer.billing_province && (
                        <p className="text-xs text-secondary-400">
                          {customer.billing_city}, {customer.billing_province}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-secondary-400">
                      {customer.project_count} project
                      {customer.project_count !== 1 ? 's' : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && <p className="error-message">{error}</p>}
    </div>
  );
}